/** Hooks for branch pipelines in the sidebar — the rule itself lives in
 *  `sidebarPipelines.logic.ts` (pure, unit-tested). */
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "@/stores/hook";
import useRouterData from "@/hooks/api/useRouterData";
import { useDynamicsRouteMeta } from "@/hooks/useDynamicsRouteMeta";
import { selectDynamicFilters } from "@/features/dynamic/store/dynamicFilterSlice";
import {
  csv,
  isPipelineGroup,
  leadsPipelineBranchId,
  type PipelineGroupLike,
  type SidebarBranchContext,
} from "./sidebarPipelines.logic";

export { applyBranchPipelineContext } from "./sidebarPipelines.logic";
export type { PipelineGroupLike, SidebarBranchContext } from "./sidebarPipelines.logic";

/**
 * The branch selection the sidebar follows: a stage page's own header picker
 * when it has one applied, otherwise the Leads page's.
 */
export function useSidebarBranchContext(): SidebarBranchContext {
  const { isDynamicsRoute, routeId } = useDynamicsRouteMeta();
  const stageRouteId = isDynamicsRoute && routeId != null ? routeId : null;

  const leadsBranch = useAppSelector((s) => s.leadFilterSlice.appliedFilters?.branchId);
  const leadsNoBranch = useAppSelector((s) => !!s.leadFilterSlice.appliedFilters?.noBranch);
  const routeBranch = useAppSelector((s) =>
    stageRouteId != null ? selectDynamicFilters(s, stageRouteId).appliedFilters?.branchId : undefined,
  );
  const routeNoBranch = useAppSelector((s) =>
    stageRouteId != null ? !!selectDynamicFilters(s, stageRouteId).appliedFilters?.noBranch : false,
  );

  return useMemo(() => {
    const routeIds = csv(routeBranch);
    const useRoute = routeIds.length > 0 || routeNoBranch;
    return {
      branchIds: useRoute ? routeIds : csv(leadsBranch),
      noBranch: useRoute ? routeNoBranch : leadsNoBranch,
      activeStageId: stageRouteId != null ? String(stageRouteId) : null,
    };
  }, [routeBranch, routeNoBranch, leadsBranch, leadsNoBranch, stageRouteId]);
}

/**
 * A branch that runs the pipeline holding this stage — what `usePipeline`
 * needs to load that pipeline. `undefined` for a default-pipeline stage (and
 * for every stage in a tenant without branch pipelines), so callers keep the
 * plain default read.
 */
export function useStagePipelineBranchId(
  stageId: string | number | null | undefined,
): number | undefined {
  const { tabs } = useRouterData();
  return useMemo(() => {
    if (stageId == null || stageId === "") return undefined;
    const sid = String(stageId);
    for (const g of (tabs?.groups ?? []) as PipelineGroupLike[]) {
      if (!isPipelineGroup(g)) continue;
      if (g.routes.some((r) => String(r?.stageId) === sid)) return g.branchIds?.[0];
    }
    return undefined;
  }, [tabs, stageId]);
}

/**
 * The branch whose pipeline the Leads page reads, from its branch selection
 * (see `leadsPipelineBranchId`): one picked branch pipeline, or the user's own
 * when it is the only pipeline they have; else `undefined` (default pipeline).
 */
export function useLeadsPipelineBranchId(enabled = true): number | undefined {
  const { tabs } = useRouterData();
  const branchId = useAppSelector((s) => s.leadFilterSlice.branchId);
  const noBranch = useAppSelector((s) => !!s.leadFilterSlice.noBranch);
  return useMemo(
    () =>
      enabled
        ? leadsPipelineBranchId((tabs?.groups ?? []) as PipelineGroupLike[], branchId, noBranch)
        : undefined,
    [enabled, tabs, branchId, noBranch],
  );
}

/**
 * The branch whose pipeline THIS page shows, for components that don't know
 * their route (filter drawer sections, chip labels):
 *   - a branch-pipeline stage page → that pipeline;
 *   - the Leads page → `useLeadsPipelineBranchId`;
 *   - anything else → `undefined` (the default pipeline, as before).
 */
export function usePagePipelineBranchId(): number | undefined {
  const { pathname } = useLocation();
  const { isDynamicsRoute, routeId } = useDynamicsRouteMeta();
  const stageBranch = useStagePipelineBranchId(isDynamicsRoute ? routeId : null);
  const onLeadsPage = pathname === "/leads" || pathname.startsWith("/leads/");
  const leadsFork = useLeadsPipelineBranchId(onLeadsPage);
  return stageBranch ?? leadsFork;
}
